import { FvButton, FvLinkButton, Icon } from '@fv/client-components'
import type { Currency, Workflow } from '@fv/models'
import type { EquipmentType } from '@fv/models/equipment'

import { DividedContent } from '@/components/DividedContent'
import InfoBox from '@/components/InfoBox'
import { Pinline } from '@/components/Pinline'
import { ShipperSlider } from '@/components/ShipperSlider'
import { useSpotQuoteEmailPreview } from '@/hooks/spotQuotes'
import { routes } from '@/routes'

import { SentQuoteRequestsHeader } from './SentQuoteRequestsHeader'

type SpotQuoteEmailPreviewProps = {
  onClose: () => void
  isOpen: boolean
  loadId: string
  equipmentTypes?: EquipmentType[]
  shipperMessage?: string
  targetPrice?: number
  targetPriceCurrency?: Currency
  bidExpirationLocal?: string
}

export const SpotQuoteEmailPreview = ({
  onClose,
  isOpen,
  loadId,
  equipmentTypes,
  shipperMessage,
  targetPrice,
  targetPriceCurrency,
  bidExpirationLocal,
}: SpotQuoteEmailPreviewProps) => {
  const previewQuery = useSpotQuoteEmailPreview({
    isOpen,
    loadId,
    equipmentTypes,
    shipperMessage,
    targetPrice,
    targetPriceCurrency,
    bidExpirationLocal,
  })

  const isLoading = previewQuery.isLoading && previewQuery.isFetching

  return (
    <ShipperSlider onClose={onClose} isOpen={isOpen}>
      <div className="mx-0 -mt-4 mb-4 flex items-center">
        <h6 className="mb-0">
          <Icon
            icon="envelope"
            className="text-fv-gray-dark"
            transform="down-1"
          />
          <span>Email preview</span>
        </h6>

        <FvButton
          className="ml-auto"
          icon="times"
          onClick={onClose}
          theme="plain"
        >
          Close
        </FvButton>
      </div>

      {isLoading && (
        <div className="flex justify-center">
          <Icon icon="sync" size="2x" />
        </div>
      )}

      {!isLoading && previewQuery.data && (
        <div
          className="border border-fv-gray mb-8 pointer-events-none pb-4"
          dangerouslySetInnerHTML={{ __html: previewQuery.data.content }}
        />
      )}
    </ShipperSlider>
  )
}

type SpotQuoteEmailConfirmationProps = {
  onClose: () => void
  emails?: string[]
  isOpen: boolean
  workflow: Workflow
}

export const SpotQuoteEmailConfirmation = ({
  onClose,
  emails,
  isOpen,
  workflow,
}: SpotQuoteEmailConfirmationProps) => {
  return (
    <ShipperSlider
      className="flex flex-col items-stretch"
      onClose={onClose}
      isOpen={isOpen}
    >
      <SentQuoteRequestsHeader emails={emails} />

      <div>
        <InfoBox className="mb-8">
          You will be able to see quotes and messages for each truck as they
          come in from your 'In process' page. You can also turn on
          notifications to be notified each time a new message or quote comes
          in.
        </InfoBox>

        <label className="label">Where would you like to go now?</label>
        <Pinline rule />

        <DividedContent className="fa-width-auto">
          <FvLinkButton icon="usd-square" to={routes.quote(workflow)}>
            Quote again
          </FvLinkButton>

          <FvLinkButton icon="warehouse" to="/pending">
            Go to In process
          </FvLinkButton>

          <FvLinkButton icon="times" onClick={onClose}>
            Stay here, close this panel
          </FvLinkButton>
        </DividedContent>
      </div>
    </ShipperSlider>
  )
}
